import React, { useState, useRef, useEffect } from 'react';
import styles from './NodeContainer.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import { useWorkflowContext } from 'app-context/worflow/WorkflowContext';
import ConfirmationModal from 'component/hoc/success-modal/ConfirmationModal';
import NodeOptions from './components/NodeOptions';
import { getConnectedEdges, useEdges } from '@xyflow/react';

const NodeContainer = ({
  children,
  name,
  initial, //for initial node styling
  id,
  onClick,
  backgroundColor, //for initial node (in case i need it to take color from branding)
  selected,
  node,
  isError,
}) => {
  const containerRef = useRef(null);
  const { workflowData, setWorkflowData, setDisableDrag } =
    useWorkflowContext();
  const edges = useEdges();
  const connectedEdges = edges.filter((edge) => edge.source === id);

  const [popoverVisible, setPopoverVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#ffffff');
  const [isEditing, setIsEditing] = useState(false);
  const [newTitle, setNewTitle] = useState(name);

  useEffect(() => {
    if (backgroundColor) {
      setSelectedColor(backgroundColor);
    }
  }, [backgroundColor]);

  // for handling delete node
  const handleDeleteNode = () => {
    setWorkflowData((prev) => {
      const deletedNode = prev.nodes.find((node) => node.id === id);
      if (!deletedNode) return prev;

      // Get all edges connected to this node
      const connectedEdges = getConnectedEdges([deletedNode], prev.edges);

      // Remove the node and its connected edges
      return {
        ...prev,
        nodes: prev.nodes.filter((node) => node.id !== id), // Remove node
        edges: prev.edges.filter((edge) => !connectedEdges.includes(edge)), // Remove its edges
      };
    });
  };

  const handleCloseModal = () => {
    setIsDeleteModalOpen(false);
  };

  //for handling right click on node (showing the dropdown options)
  const handleRightClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPopoverVisible(true);
    document.dispatchEvent(
      new CustomEvent('closeAllPopovers', { detail: { id } })
    );
  };

  const handleClickOutside = (e) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target) &&
      !e.target.closest(`.${styles.colorPicker}`)
    ) {
      setPopoverVisible(false);
      setColorPickerVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    const closePopoverHandler = (event) => {
      if (event.detail.id !== id) {
        setPopoverVisible(false);
        setColorPickerVisible(false);
      }
    };
    document.addEventListener('closeAllPopovers', closePopoverHandler);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('closeAllPopovers', closePopoverHandler);
    };
  }, [id]);

  //for handling node title
  const handleEditName = () => {
    setIsEditing(true);
    setPopoverVisible(false);
  };
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      updateNodeTitle();
    }
  };
  const updateNodeTitle = () => {
    setWorkflowData((prev) => ({
      ...prev,
      nodes: prev.nodes?.map((node) =>
        node.id === id ? { ...node, title: newTitle } : node
      ),
    }));
  };

  //for handling node color
  const handleOpenChromeColor = (e) => {
    e.stopPropagation();
    setColorPickerVisible(true);
    if (setDisableDrag) setDisableDrag(false);
    setPopoverVisible(false);
  };
  const handleColorChange = (color) => {
    setSelectedColor(color.hex);
    handleNodeColor();
  };
  const handleNodeColor = () => {
    const updatedNodes = workflowData?.nodes?.map((node) =>
      node.id === id ? { ...node, color: selectedColor } : node
    );
    setWorkflowData((prev) => ({
      ...prev,
      nodes: updatedNodes,
    }));
  };

  //for duplicating
  const handleDuplicateNode = () => {
    const duplicatedNode = workflowData?.nodes?.find((node) => node.id === id);
    if (!duplicatedNode) return;
    const newId = Math.max(...workflowData.nodes.map((node) => node.id), 0) + 1;
    const editDuplicateNode = {
      ...duplicatedNode,
      id: newId.toString(),
      position: {
        x: duplicatedNode?.position?.x + 200,
        y: duplicatedNode?.position?.y + 100,
      },
    };
    setWorkflowData((prev) => ({
      ...prev,
      nodes: [...prev.nodes, editDuplicateNode],
    }));
  };

  //this to close nodeOptions when clicking outside the node
  useEffect(() => {
    if (!selected) {
      if (setDisableDrag) setDisableDrag(true);
      setColorPickerVisible(false);
      setPopoverVisible(false);
      updateNodeTitle();
      setIsEditing(false);
    }
  }, [selected]);

  useEffect(() => {
    setNewTitle(name);
  }, [name]);

  return (
    <>
      <div
        ref={containerRef}
        className={`${styles.cont} ${
          node?.data?.isNew ? styles['new-node'] : ''
        }  ${
          initial && isError
            ? styles.initialErrorNode
            : initial
            ? styles.initialNode
            : ''
        }  ${isError ? styles['error'] : ''}`}
        onContextMenu={handleRightClick}
        style={{
          backgroundColor: selectedColor,
        }}
        onClick={onClick}
      >
        <NodeOptions
          popoverVisible={popoverVisible}
          handleOpenChromeColor={handleOpenChromeColor}
          handleColorChange={handleColorChange}
          colorPickerVisible={colorPickerVisible}
          selectedColor={selectedColor}
          handleEditName={handleEditName}
          handleDuplicateNode={handleDuplicateNode}
          initial={initial}
          setDisableDrag={setDisableDrag}
        />

        {!initial && (
          <div
            className={styles.title}
            onClick={() => setPopoverVisible(false)}
          >
            {isEditing ? (
              <input
                className={styles.input}
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                autoFocus
              />
            ) : (
              <p>{newTitle}</p>
            )}

            <div>
              <SvgWithBranding
                type="delete_table"
                color="#787878"
                className={styles.deleteIcon}
                onClick={(e) => (
                  e.stopPropagation(), setIsDeleteModalOpen(true)
                )}
              />
            </div>
          </div>
        )}
        <div
          className={`${styles.children} ${initial ? styles.initialChild : ''}`}
        >
          {children}
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleDeleteNode}
        closeOnSubmit={true}
        onApproveButton={{ text: 'delete' }}
        text={`Delete Node`}
        type="Delete"
      />
    </>
  );
};

export default NodeContainer;
